import React from 'react';
import {
  Layers,
  SlidersHorizontal,
  Plug,
  HeadphonesIcon,
  BarChart3,
  Palette,
  Target,
  ClipboardCheck,
  FileCode2,
  Coins,
  LineChart,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const screenFeatures = [
  { icon: Layers, title: 'Bulk Screening', description: 'Screen 500+ submissions before breakfast' },
  { icon: SlidersHorizontal, title: 'Custom Criteria', description: 'Your standards, applied to every paper' },
  { icon: Plug, title: 'API Integration', description: 'Plugs into your existing workflow' },
  { icon: BarChart3, title: 'Submission Analytics', description: 'Spot common weaknesses across your pool' },
  { icon: Palette, title: 'White-Label Options', description: 'Branded experience for your authors' },
  { icon: HeadphonesIcon, title: 'Dedicated Support', description: 'Onboarding help and ongoing support' },
];

const shapeFeatures = [
  {
    icon: Target,
    title: 'Your rubric, in the editor',
    description: 'Authors see their draft measured against your published criteria before they submit',
  },
  {
    icon: ClipboardCheck,
    title: 'Submission-readiness check',
    description: 'Format, missing sections, ethics and data statements, reference integrity — caught pre-deadline',
  },
  {
    icon: FileCode2,
    title: 'Your LaTeX template',
    description: 'Your official class file as a starting point, so formatting compliance is automatic',
  },
  {
    icon: Coins,
    title: 'Sponsored author credits',
    description: 'Fund a credit pool so your authors get review passes free during the submission window',
  },
  {
    icon: LineChart,
    title: 'Cohort insight, pre-deadline',
    description: 'Anonymised view of where the incoming cohort is weakest, while it can still be fixed',
  },
  {
    icon: ShieldCheck,
    title: 'Integrity and provenance',
    description: 'AI assistance is disclosed per document. EU/EEA processing throughout',
  },
];

const FeatureGrid: React.FC<{
  items: { icon: React.ComponentType<{ className?: string }>; title: string; description: string }[];
}> = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
    {items.map((feature) => (
      <div key={feature.title} className="flex flex-col items-center text-center gap-2">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <feature.icon className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h4 className="text-sm font-medium">{feature.title}</h4>
          <p className="text-xs text-muted-foreground">{feature.description}</p>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Venue pitch for the unified surface: triage what arrives, and influence what
 * gets written in the first place now that authors draft inside PaperVerse.
 */
export const EnterpriseSectionV2 = () => {
  return (
    <div className="max-w-5xl mx-auto w-full space-y-6">
      <Card className="p-8 md:p-10">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-semibold mb-2">Screen what arrives</h3>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Automated initial screening for conferences and journals. Your reviewers focus
            on qualified submissions.
          </p>
        </div>
        <FeatureGrid items={screenFeatures} />
      </Card>

      <Card className="p-8 md:p-10">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-semibold mb-2">Shape what gets submitted</h3>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Authors now write their papers with us. That means your venue can raise the
            quality of a submission before it exists — instead of paying reviewers to
            reject avoidable problems.
          </p>
        </div>
        <FeatureGrid items={shapeFeatures} />
        <p className="mt-8 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
          Venue programmes are set up with you: an annual platform fee plus a credit pool,
          scoped to your submission volume. We agree the exact rubric, templates and
          integrations during onboarding.
        </p>
      </Card>

      <div className="flex justify-center">
        <Button asChild>
          <a href="mailto:contact@paperverse.io">
            Schedule a Demo
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </div>
    </div>
  );
};

export default EnterpriseSectionV2;
